import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * PLAT-USER-NAME-1: a person's name is mandatory. Shown over the app when /me
 * says `name_missing`; saves through the existing PATCH /v1/account, then
 * refreshes the user so the gate clears. Text approved 2026-10-08.
 */
export function NameGate() {
  const { refreshUser } = useAuth()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || trimmed.includes('@')) {
      setError('Please enter your name.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await apiClient.patch('/v1/account', { name: trimmed })
      await refreshUser()
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-pal-overlay/80 p-4" role="presentation">
      <Card className="w-full max-w-md" role="dialog" aria-modal="true" aria-labelledby="name-gate-title">
        <form onSubmit={handleSave}>
          <CardHeader className="space-y-3 border-b">
            <CardTitle id="name-gate-title" className="text-2xl">Please tell us your name</CardTitle>
            <CardDescription className="text-base">
              We use your name on your reports and when we greet you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 py-6">
            <Label htmlFor="name-gate-input">Your full name</Label>
            <Input
              id="name-gate-input"
              autoFocus
              autoComplete="name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          </CardContent>
          <CardFooter className="justify-end">
            <Button type="submit" disabled={saving}>Save</Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
